import { useState, useEffect } from 'react';
import { supabase } from '../../lib/db';
import { logger } from '../../services/logger';
import { 
  Trophy, 
  Calendar, 
  DollarSign, 
  Save, 
  Loader2, 
  AlertCircle,
  CheckCircle,
  Eye,
  RefreshCw,
  PlusCircle,
  Archive
} from 'lucide-react';

// Helper to convert UTC date to local datetime-local format
const toLocalDateTimeString = (dateString: string | null): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  // Get local date components
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

interface CompetitionConfig {
  id: string;
  promo_name: string;
  is_active: boolean;
  is_archived?: boolean;
  starting_balance: number;
  start_date: string | null;
  end_date: string | null;
  show_leaderboard: boolean;
  show_profit_leaderboard: boolean;
  show_affiliate_leaderboard: boolean;
  show_user_affiliate_count: boolean;
  show_admin_profit_leaderboard: boolean;
  show_admin_affiliate_leaderboard: boolean;
}

export function CompetitionSettings() {
  const [config, setConfig] = useState<CompetitionConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isResettingWallets, setIsResettingWallets] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    promo_name: '',
    is_active: true,
    starting_balance: 10000,
    start_date: '',
    end_date: '',
    show_leaderboard: true,
    show_profit_leaderboard: true,
    show_affiliate_leaderboard: true,
    show_user_affiliate_count: false,
    show_admin_profit_leaderboard: true,
    show_admin_affiliate_leaderboard: true
  });

  // New competition form state
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newCompetition, setNewCompetition] = useState({
    promo_name: '',
    starting_balance: 10000,
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchCompetitionConfig();
  }, []);

  const fetchCompetitionConfig = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('promo_config')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (data) {
        setConfig(data as CompetitionConfig);
        setFormData({
          promo_name: data.promo_name || '',
          is_active: data.is_active ?? true,
          starting_balance: data.starting_balance || 10000,
          start_date: toLocalDateTimeString(data.start_date),
          end_date: toLocalDateTimeString(data.end_date),
          show_leaderboard: data.show_leaderboard ?? true,
          show_profit_leaderboard: data.show_profit_leaderboard ?? true,
          show_affiliate_leaderboard: data.show_affiliate_leaderboard ?? true,
          show_user_affiliate_count: data.show_user_affiliate_count ?? false,
          show_admin_profit_leaderboard: data.show_admin_profit_leaderboard ?? true,
          show_admin_affiliate_leaderboard: data.show_admin_affiliate_leaderboard ?? true
        });
      }
    } catch (err) {
      logger.error('Error fetching competition config:', err);
      setError('Failed to load competition settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config?.id) {
      setError('No active competition found');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      const { data, error: updateError } = await supabase.rpc('admin_update_competition_settings', {
        p_competition_id: config.id,
        p_promo_name: formData.promo_name,
        p_is_active: formData.is_active,
        p_starting_balance: formData.starting_balance,
        p_start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        p_end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        p_show_leaderboard: formData.show_leaderboard,
        p_show_profit_leaderboard: formData.show_profit_leaderboard,
        p_show_affiliate_leaderboard: formData.show_affiliate_leaderboard,
        p_show_user_affiliate_count: formData.show_user_affiliate_count,
        p_show_admin_profit_leaderboard: formData.show_admin_profit_leaderboard,
        p_show_admin_affiliate_leaderboard: formData.show_admin_affiliate_leaderboard
      });

      if (updateError) throw updateError;
      
      const result = data as { success: boolean; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to update settings');
      }

      setSuccess('Competition settings updated successfully');
      await fetchCompetitionConfig();
    } catch (err) {
      logger.error('Error saving competition settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetWallets = async () => {
    if (!config?.id) {
      setError('No active competition found');
      return;
    }

    const confirmed = window.confirm(
      `⚠️ WARNING: This will reset ALL user wallets to R${formData.starting_balance}.\n\n` +
      `Excluded accounts: reece@wayja.me, devon@wayja.me\n\n` +
      `This action cannot be undone. Are you sure you want to proceed?`
    );

    if (!confirmed) return;

    try {
      setIsResettingWallets(true);
      setError(null);
      setSuccess(null);

      const { data, error: resetError } = await supabase.rpc('admin_reset_wallets_for_competition', {
        p_competition_id: config.id,
        p_excluded_emails: ['reece@wayja.me', 'devon@wayja.me']
      });

      if (resetError) throw resetError;
      
      const result = data as { success: boolean; error?: string; message?: string; users_updated?: number };
      if (!result.success) {
        throw new Error(result.error || 'Failed to reset wallets');
      }

      setSuccess(result.message || `Successfully reset wallets for ${result.users_updated} users`);
    } catch (err) {
      logger.error('Error resetting wallets:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset wallets');
    } finally {
      setIsResettingWallets(false);
    }
  };

  const handleArchive = async () => {
    if (!config?.id) {
      setError('No active competition found');
      return;
    }

    const confirmed = window.confirm(
      `⚠️ WARNING: This will archive the competition "${config.promo_name}".\n\n` +
      `The competition will be:\n` +
      `- Deactivated (no longer active)\n` +
      `- Archived for historical reference\n` +
      `- Visible in the Historical Competitions section\n\n` +
      `Are you sure you want to archive this competition?`
    );

    if (!confirmed) return;

    try {
      setIsArchiving(true);
      setError(null);
      setSuccess(null);

      const { data, error: archiveError } = await supabase.rpc('admin_archive_competition', {
        p_competition_id: config.id
      });

      if (archiveError) throw archiveError;
      
      const result = data as { success: boolean; error?: string; message?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to archive competition');
      }

      setSuccess(result.message || 'Competition archived successfully');
      await fetchCompetitionConfig();
    } catch (err) {
      logger.error('Error archiving competition:', err);
      setError(err instanceof Error ? err.message : 'Failed to archive competition');
    } finally {
      setIsArchiving(false);
    }
  };

  const handleCreateCompetition = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      if (!newCompetition.promo_name || !newCompetition.start_date || !newCompetition.end_date) {
        throw new Error('Please fill in all required fields');
      }

      const { data, error: createError } = await supabase.rpc('admin_create_competition', {
        p_promo_name: newCompetition.promo_name,
        p_starting_balance: newCompetition.starting_balance,
        p_start_date: new Date(newCompetition.start_date).toISOString(),
        p_end_date: new Date(newCompetition.end_date).toISOString(),
        p_competition_terms: null
      });

      if (createError) throw createError;
      
      const result = data as { success: boolean; error?: string; competition_id?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to create competition');
      }

      setSuccess('Competition created successfully! You can now reset wallets for this competition.');
      setIsCreatingNew(false);
      setNewCompetition({ promo_name: '', starting_balance: 10000, start_date: '', end_date: '' });
      await fetchCompetitionConfig();
    } catch (err) {
      logger.error('Error creating competition:', err);
      setError(err instanceof Error ? err.message : 'Failed to create competition');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-wayja-teal" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="bg-wayja-teal/20 p-3 rounded-full">
          <Trophy className="w-6 h-6 text-wayja-teal" />
        </div>
        <div>
          <h2 className="text-2xl font-bold">Competition Settings</h2>
          <p className="text-gray-500 text-sm">
            Configure competition parameters and leaderboard visibility
          </p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}
      
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-600 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* Create New Competition Section */}
      {!config && !isCreatingNew && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">No Active Competition</h3>
          <p className="text-yellow-700 mb-4">Create a new competition to get started.</p>
          <button
            onClick={() => setIsCreatingNew(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-wayja-teal text-white rounded-lg hover:bg-wayja-dark-teal transition-colors"
          >
            <PlusCircle className="w-5 h-5" />
            Create New Competition
          </button>
        </div>
      )}

      {/* New Competition Form */}
      {isCreatingNew && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Create New Competition</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Competition Name *
              </label>
              <input
                type="text"
                value={newCompetition.promo_name}
                onChange={(e) => setNewCompetition({ ...newCompetition, promo_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                placeholder="e.g., January 2026 Trading Competition"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Starting Balance (R) *
                </label>
                <input
                  type="number"
                  value={newCompetition.starting_balance}
                  onChange={(e) => setNewCompetition({ ...newCompetition, starting_balance: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date & Time *
                </label>
                <input
                  type="datetime-local"
                  value={newCompetition.start_date}
                  onChange={(e) => setNewCompetition({ ...newCompetition, start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date & Time *
                </label>
                <input
                  type="datetime-local"
                  value={newCompetition.end_date}
                  onChange={(e) => setNewCompetition({ ...newCompetition, end_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => setIsCreatingNew(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCompetition}
                disabled={isSaving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-wayja-teal text-white rounded-lg hover:bg-wayja-dark-teal disabled:opacity-50 transition-colors"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
                Create Competition
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Existing Competition Settings */}
      {config && (
        <>
          {/* Basic Settings */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-wayja-teal" />
              Basic Settings
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Competition Name
                </label>
                <input
                  type="text"
                  value={formData.promo_name}
                  onChange={(e) => setFormData({ ...formData, promo_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <DollarSign className="w-4 h-4 inline mr-1" />
                    Starting Balance (R)
                  </label>
                  <input
                    type="number"
                    value={formData.starting_balance}
                    onChange={(e) => setFormData({ ...formData, starting_balance: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-700">Competition Active</label>
                  <p className="text-xs text-gray-500">Enable or disable the competition</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-wayja-teal/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-wayja-teal"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Leaderboard Visibility */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-wayja-teal" />
              Leaderboard Visibility
            </h3>
            
            <div className="space-y-4">
              {/* Master Toggle */}
              <div className="flex items-center justify-between p-4 bg-wayja-teal/10 rounded-lg border border-wayja-teal/20">
                <div>
                  <label className="text-sm font-medium text-wayja-dark-teal">Show Leaderboards</label>
                  <p className="text-xs text-wayja-teal">Master toggle for all leaderboards</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.show_leaderboard}
                    onChange={(e) => setFormData({ ...formData, show_leaderboard: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-wayja-teal/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-wayja-teal"></div>
                </label>
              </div>

              {/* User Visibility */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">User View</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <label className="text-sm text-gray-700">Profit Leaderboard</label>
                      <p className="text-xs text-gray-500">Top traders by profit</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.show_profit_leaderboard}
                        onChange={(e) => setFormData({ ...formData, show_profit_leaderboard: e.target.checked })}
                        className="sr-only peer"
                        disabled={!formData.show_leaderboard}
                      />
                      <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 ${!formData.show_leaderboard ? 'opacity-50' : ''}`}></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <label className="text-sm text-gray-700">Affiliate Leaderboard</label>
                      <p className="text-xs text-gray-500">Top referrers</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.show_affiliate_leaderboard}
                        onChange={(e) => setFormData({ ...formData, show_affiliate_leaderboard: e.target.checked })}
                        className="sr-only peer"
                        disabled={!formData.show_leaderboard}
                      />
                      <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600 ${!formData.show_leaderboard ? 'opacity-50' : ''}`}></div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Admin Visibility */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Admin View</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <label className="text-sm text-gray-700">Admin Profit Leaderboard</label>
                      <p className="text-xs text-gray-500">Show in admin dashboard</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.show_admin_profit_leaderboard}
                        onChange={(e) => setFormData({ ...formData, show_admin_profit_leaderboard: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <label className="text-sm text-gray-700">Admin Affiliate Leaderboard</label>
                      <p className="text-xs text-gray-500">Show in admin dashboard</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.show_admin_affiliate_leaderboard}
                        onChange={(e) => setFormData({ ...formData, show_admin_affiliate_leaderboard: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Special Settings */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Special Settings</h4>
                <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div>
                    <label className="text-sm text-gray-700">Show Affiliate Count to Users</label>
                    <p className="text-xs text-yellow-700">⚠️ By default, users only see their rank position</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.show_user_affiliate_count}
                      onChange={(e) => setFormData({ ...formData, show_user_affiliate_count: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Wallet Management */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-wayja-teal" />
              Wallet Management
            </h3>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Warning: Destructive Action</p>
                  <p className="text-xs text-red-700 mt-1">
                    Resetting wallets will set ALL user balances to R{formData.starting_balance}. 
                    This cannot be undone. The following accounts will be excluded:
                  </p>
                  <ul className="text-xs text-red-700 mt-2 list-disc list-inside">
                    <li>reece@wayja.me</li>
                    <li>devon@wayja.me</li>
                  </ul>
                </div>
              </div>
            </div>

            <button
              onClick={handleResetWallets}
              disabled={isResettingWallets}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {isResettingWallets ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Resetting Wallets...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Reset All Wallets to R{formData.starting_balance}
                </>
              )}
            </button>
          </div>

          {/* Archive Competition */}
          {config && !config.is_archived && (
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-400">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Archive className="w-5 h-5 text-orange-600" />
                Archive Competition
              </h3>
              
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-orange-800">Archive for Historical Reference</p>
                    <p className="text-xs text-orange-700 mt-1">
                      Archiving will deactivate the competition and move it to the Historical Competitions section.
                      You can view archived competitions later for reference.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleArchive}
                disabled={isArchiving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors"
              >
                {isArchiving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Archiving...
                  </>
                ) : (
                  <>
                    <Archive className="w-4 h-4" />
                    Archive Competition
                  </>
                )}
              </button>
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <button
              onClick={fetchCompetitionConfig}
              disabled={isSaving}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Reset
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-6 py-2 bg-wayja-teal text-white rounded-lg hover:bg-wayja-dark-teal disabled:opacity-50 transition-colors"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

